import { useState } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { requestPasswordToken } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import ErrorModal from "@/components/ErrorModal";
import { extractApiError } from "@/lib/extractApiError";

const resetSchema = z.object({
  email: z
    .string()
    .min(1, "Bitte gib deine E-Mail-Adresse ein.")
    .email("Bitte gib eine gültige E-Mail-Adresse ein."),
});

const ResetPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (data) => {
    setError(null);
    setIsLoading(true);
    try {
      await requestPasswordToken({
        consumer: { emailAddress: data.email },
        reset: {
          return: {
            url: `${window.origin}/set-password?resetToken=`,
          },
        },
        migration: {
          return: {
            url: `${window.origin}/migrate?migrateToken=`,
          },
        },
      });
      setSent(true);
    } catch (err) {
      const apiError = err.response?.data;
      if (apiError?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(apiError.data.details, navigate);
      } else {
        setError(extractApiError(apiError));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5">
      <div className="flex flex-col gap-4 items-center w-full">
        <h1 className="text-black text-center text-[32px] md:text-[40px]">
          Passwort zurücksetzen
        </h1>
        {sent ? (
          <p className="text-black text-center text-[18px] font-medium leading-[1.4]">
            Wir haben einen Link an{" "}
            <span className="font-extrabold">{getValues("email")}</span>{" "}
            gesendet. Bitte prüfe dein Postfach.
          </p>
        ) : (
          <p className="text-black text-center text-[18px] font-medium leading-[1.4]">
            Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum
            Zurücksetzen.
          </p>
        )}
      </div>

      {!sent ? (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 items-center w-full max-w-[480px]"
        >
          <div className="w-full">
            <Input
              type="email"
              label="E-Mail"
              placeholder="E-Mail-Adresse eingeben"
              autoComplete="email"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-primary text-[13px] font-medium mt-1">
                {errors.email.message}
              </p>
            )}
          </div>

          <ErrorModal
            open={!!error}
            onClose={() => setError(null)}
            message={error?.message}
            errorCode={error?.errorCode}
          />

          <Button
            type="submit"
            className="w-full md:h-[54px] md:text-[24px] md:rounded-[14px]"
            size="sm"
            disabled={isLoading}
          >
            {isLoading ? "Bitte warte..." : "Link senden"}
          </Button>

          <button
            type="button"
            onClick={() => navigate("/")}
            className="font-extrabold text-[18px] text-black uppercase hover:underline leading-[28px] min-h-[40px]"
          >
            Zurück zur Anmeldung
          </button>
        </form>
      ) : (
        <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
          <Button
            type="button"
            className="w-full md:h-[54px] md:text-[24px] md:rounded-[14px]"
            size="sm"
            onClick={() => navigate("/")}
          >
            Zur Anmeldung
          </Button>
        </div>
      )}
    </div>
  );
};

export default ResetPage;
